import React from "react";
import { Form, FormControl, FormGroup } from "react-bootstrap";
import { InputBox } from "../../components";
import { AccountInterface, ApiHelper, UserHelper, UserInterface, ToastHelper } from "../../helpers";

interface Props {

}

export const AccountSettings: React.FC<Props> = props => {

  const [user, setUser] = React.useState<UserInterface>(null);
  const [account, setAccount] = React.useState<AccountInterface>(null);

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [verifyPassword, setVerifyPassword] = React.useState("");
  const [accountName, setAccountName] = React.useState("");
  const [address1, setAddress1] = React.useState("");
  const [address2, setAddress2] = React.useState("");
  const [city, setCity] = React.useState("");
  const [state, setState] = React.useState("");
  const [zip, setZip] = React.useState("");
  const [country, setCountry] = React.useState("");
  const [sendReceipts, setSendReceipts] = React.useState(false);

  const [status, setStatus] = React.useState("");
  const [referredBy, setReferredBy] = React.useState<number | null>(null);
  const [referredByCode, setReferredByCode] = React.useState<string>("");
  const [referredByOptions, setReferredByOptions] = React.useState<{ referredBy: number | null, referredByCode: string | null }[]>([]);

  const init = () => {
    ApiHelper.get("/users/" + UserHelper.user.id).then((data: UserInterface) => { setUser(data); setEmail(data.email) });
    ApiHelper.get("/accounts/" + UserHelper.currentDepartment.accountId).then((data: AccountInterface) => {
      setAccount(data);
      setAccountName(data.name)
      setAddress1(data.address1);
      setAddress2(data.address2);
      setCity(data.city);
      setState(data.state);
      setZip(data.zip);
      setCountry(data.country);
      setSendReceipts(data.sendReceipts || false);
      setStatus(data.status);
      if (UserHelper.checkAccess("admin")) {
        setReferredBy(data.referredBy || null);
        setReferredByCode(data.referredByCode || "");
      }
    });
    if (UserHelper.checkAccess("admin")) {
      ApiHelper.get<{ referredBy: number | null, referredByCode: string | null }[]>("/accounts/referredByOptions").then((data) => {
        setReferredByOptions(data || []);
      });
    }
  }

  React.useEffect(init, [])


  const handleChange = (e: any) => {
    let val = e.currentTarget.value;
    switch (e.currentTarget.id) {
      case "accountName": setAccountName(val); break;
      case "address1": setAddress1(val); break;
      case "address2": setAddress2(val); break;
      case "city": setCity(val); break;
      case "state": setState(val); break;
      case "zip": setZip(val); break;
      case "country": setCountry(val); break;
      case "email": setEmail(val); break;
      case "password": setPassword(val); break;
      case "verifyPassword": setVerifyPassword(val); break;
      case "status": setStatus(val); break;
      case "referredByCode": setReferredByCode(val); break;
    }
  }

  const handleReferredByChange = (e: any) => {
    const selectedValue = e.currentTarget.value;
    if (selectedValue === "") {
      setReferredBy(null);
      setReferredByCode("");
    } else {
      const selected = referredByOptions.find(opt =>
        `${opt.referredBy || ""}_${opt.referredByCode || ""}` === selectedValue
      );
      if (selected) {
        setReferredBy(selected.referredBy);
        setReferredByCode(selected.referredByCode || "");
      }
    }
  }

  const validate = () => {
    const emailValidator = /\S+@\S+\.\S+/;
    const errors: string[] = [];
    if (accountName === "") errors.push("Account name cannot be blank.");
    if (email === "") errors.push("Email cannot be blank.");
    else if (!emailValidator.test(email)) errors.push("Invalid email address.");
    if (password !== "") {
      if (password !== verifyPassword) errors.push("Passwords do not match");
    }

    if (errors.length > 0) {
      errors.forEach(error => ToastHelper.error(error));
      return false;
    }
    return true;
  }


  const handleSave = () => {
    if (validate()) {
      if (accountName !== account.name || address1 !== account.address1 || address2 !== account.address2 || city !== account.city || state !== account.state || zip !== account.zip || country !== account.country || sendReceipts !== (account.sendReceipts || false)) {
        ApiHelper.post("/accounts/updateDetails", { name: accountName, address1, address2, city, state, zip, country, sendReceipts }).then(() => {
          ToastHelper.success("Account details saved successfully");
        });
      }

      if (status !== account.status) {
        ApiHelper.post("/accounts/updateStatus", { status: status }).then(() => {
          ToastHelper.success("Account status updated");
        });
      }

      if (UserHelper.checkAccess("admin") && (referredBy !== account.referredBy || referredByCode !== account.referredByCode)) {
        ApiHelper.post("/accounts/updateReferredBy", { accountId: account.id, referredBy, referredByCode }).then(() => {
          ToastHelper.success("Referred by information updated");
        });
      }

      if (email !== user.email) {
        ApiHelper.post("/users/updateEmail", { email: email }).then(() => {
          ToastHelper.success("Email updated successfully");
        });
      }

      if (password !== "") {
        ApiHelper.post("/users/updatePassword", { password: password }).then(() => {
          ToastHelper.success("Password updated successfully");
        });
      }
    }
  }

  const getAdminSettings = () => {
    if (UserHelper.checkAccess("admin")) {
      const currentValue = `${referredBy || ""}_${referredByCode || ""}`;
      return (
        <div className="account-settings-section">
          <div className="account-settings-section-label">Admin</div>

          <FormGroup className="account-settings-field">
            <label className="account-settings-field-label" htmlFor="status">Status</label>
            <FormControl data-testid="account-status-select" as="select" id="status" value={status} onChange={handleChange}>
              <option value="preview">Preview</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </FormControl>
          </FormGroup>

          <FormGroup className="account-settings-field">
            <label className="account-settings-field-label" htmlFor="referredByCode">Referred By</label>
            <FormControl data-testid="referred-by-select" as="select" id="referredByCode" value={currentValue} onChange={handleReferredByChange}>
              <option value="">-- None --</option>
              {Array.isArray(referredByOptions) && referredByOptions.map((opt, index) => {
                const value = `${opt.referredBy || ""}_${opt.referredByCode || ""}`;
                const label = opt.referredByCode || `ID: ${opt.referredBy || ""}`;
                return <option key={index} value={value}>{label}</option>;
              })}
            </FormControl>
          </FormGroup>
        </div>
      )
    }
  }

  const passwordMismatch = verifyPassword.length > 0 && password !== verifyPassword;

  return (
    <InputBox headerText="Account Settings" headerIcon="fas fa-cog" saveFunction={handleSave}>

      {/* Account Info */}
      <div className="account-settings-section">
        <div className="account-settings-section-label">Account Info</div>

        <FormGroup className="account-settings-field">
          <label className="account-settings-field-label" htmlFor="accountName">
            Account Name<em className="account-settings-required" aria-hidden="true">*</em>
          </label>
          <FormControl data-testid="account-name-input" id="accountName" value={accountName} onChange={handleChange} />
        </FormGroup>
      </div>

      {/* Mailing Address */}
      <div className="account-settings-section">
        <div className="account-settings-section-label">Mailing Address</div>

        <FormGroup className="account-settings-field">
          <label className="account-settings-field-label" htmlFor="address1">Address Line 1</label>
          <FormControl data-testid="address1-input" id="address1" value={address1} onChange={handleChange} />
        </FormGroup>

        <FormGroup className="account-settings-field">
          <label className="account-settings-field-label" htmlFor="address2">Address Line 2</label>
          <FormControl data-testid="address2-input" id="address2" value={address2} onChange={handleChange} />
        </FormGroup>

        <div className="account-settings-address-row">
          <FormGroup className="account-settings-field">
            <label className="account-settings-field-label" htmlFor="city">City</label>
            <FormControl data-testid="city-input" id="city" value={city} onChange={handleChange} />
          </FormGroup>
          <FormGroup className="account-settings-field">
            <label className="account-settings-field-label" htmlFor="state">State</label>
            <FormControl data-testid="state-input" id="state" value={state} onChange={handleChange} />
          </FormGroup>
          <FormGroup className="account-settings-field">
            <label className="account-settings-field-label" htmlFor="zip">Zip</label>
            <FormControl data-testid="zip-input" id="zip" value={zip} onChange={handleChange} />
          </FormGroup>
        </div>

        <FormGroup className="account-settings-field">
          <label className="account-settings-field-label" htmlFor="country">Country</label>
          <FormControl data-testid="country-input" id="country" value={country} onChange={handleChange} />
        </FormGroup>
      </div>

      {/* Preferences */}
      <div className="account-settings-section">
        <div className="account-settings-section-label">Preferences</div>

        <FormGroup className="account-settings-field">
          <Form.Check
            type="switch"
            id="sendReceipts"
            label="Send payment receipts via email"
            checked={sendReceipts}
            onChange={(e) => setSendReceipts(e.target.checked)}
          />
        </FormGroup>
      </div>

      {/* Sign-in */}
      <div className="account-settings-section">
        <div className="account-settings-section-label">Sign-in</div>

        <FormGroup className="account-settings-field">
          <label className="account-settings-field-label" htmlFor="email">
            Email<em className="account-settings-required" aria-hidden="true">*</em>
          </label>
          <FormControl data-testid="settings-email-input" id="email" value={email} onChange={handleChange} />
        </FormGroup>

        <div className="account-settings-password-row">
          <FormGroup className="account-settings-field">
            <label className="account-settings-field-label" htmlFor="password">New Password</label>
            <FormControl data-testid="settings-password-input" id="password" type="password" value={password} onChange={handleChange} />
          </FormGroup>
          <FormGroup className="account-settings-field">
            <label className="account-settings-field-label" htmlFor="verifyPassword">Confirm Password</label>
            <FormControl
              data-testid="verify-password-input"
              id="verifyPassword"
              type="password"
              value={verifyPassword}
              onChange={handleChange}
              isInvalid={passwordMismatch}
            />
            <div className={`account-settings-pw-hint${passwordMismatch ? " mismatch" : ""}`}>
              {passwordMismatch ? "Passwords do not match" : "Leave blank to keep current password"}
            </div>
          </FormGroup>
        </div>
      </div>

      {getAdminSettings()}

    </InputBox>
  );

};
